export default customElements.define('w-plan-card', class PlanCard extends wealthify.CustomElementBaseClass {
    constructor() {
        const config = {
            hasChildren: true,
            cacheDom: ['data-metrics', 'data-secondary-metrics', 'data-metric', 'data-info-container', 'data-info'],
        };

        super(config);

        this.onConnectedCallback = () => {
            this.#validateSecondaryMetricSlots();
            this.#setSecondaryMetricsVisibilityAndStyles();
            this.#setInfoContainerVisibility();
            this.#toggleContinueButton();
            this.#toggleRemoveButton();
            this.dom.planLink.addEventListener('click', this.events.continuePlanClick.dispatch);
            this.dom.planRemove.addEventListener('click', this.events.removePlanClick.dispatch);
        };
    }

    static styleSelector = 'c-plan-card';
    static template = `<w-card data-root>
                        <div class="${this.styleSelector}__container">
                            <div class="${this.styleSelector}__type" data-type>
                                <w-icon size="150" class="${this.styleSelector}__type-icon" data-plan-icon></w-icon>
                                <span class="${this.styleSelector}__type-text" aria-hidden="true" data-type-label></span>
                            </div>
                            <h3 class="${this.styleSelector}__title" data-name></h3>

                            <div class="${this.styleSelector}__content">
                                <div class="${this.styleSelector}__content-row u-display__none" data-metrics>
                                    <div class="${this.styleSelector}__metrics--primary">
                                        <div class="${this.styleSelector}__metric ${this.styleSelector}__metric--primary">
                                            <h4 class="${this.styleSelector}__metric-heading" data-value-heading></h4>
                                            <p class="${this.styleSelector}__metric-value" data-value></p>
                                        </div>
                                    </div>

                                    <div class="${this.styleSelector}__metrics--secondary u-display__none" data-secondary-metrics>
                                        <span class="${this.styleSelector}__metric ${this.styleSelector}__metric--secondary" data-slot="metric" data-slot-multiple data-metric></span>
                                    </div>
                                </div>

                                <div class="${this.styleSelector}__content-row" data-info-row>
                                    <div class="${this.styleSelector}__info u-display__none" data-info-container>
                                        <w-chip label="Risk level" class="u-display__none" data-risk-level></w-chip>
                                        <w-chip label="Theme" class="u-display__none" data-theme></w-chip>
                                        <span class="${this.styleSelector}__info-slot-container u-display__none" data-slot="info" data-info></span>
                                    </div>

                                    <w-button-2 label="Remove" variant="secondary" iconright="cross" class="${this.styleSelector}__button" hide="true" data-plan-remove></w-button-2>
                                    <w-button-2 label="View" variant="secondary" iconright="caret-forward" class="${this.styleSelector}__button" data-plan-link></w-button-2>
                                </div>
                            </div>
                        </div>
                    </w-card>`;

    static themeIconMap = {
        [wealthify.enums.theme.Original]: '',
        [wealthify.enums.theme.Ethical]: 'leaf'
    };

    static accountTypeLabelMap = {
        InstantAccessCash: 'Balance',
        Contributor: 'Total amount contributed'
    };

    static customPlanContent = {
        Contributor: {
            typeName: `${wealthify.enums.accountTypeName.JISA}<p class="u-text__weight--regular u-text__transform--none">Contributor</p>`,
            type: wealthify.enumsLocal.planType.JISA,
        },
        InstantAccessCash: {
            typeName: '',
            name: wealthify.enums.accountTypeName.InstantAccessCash,
        },
        CashISA: {
            typeName: '',
            name: wealthify.enums.accountTypeName.CashISA,
        }
    };

    static disabledPlanStates = [
        wealthify.enums.portfolioStatus.Created,
        wealthify.enums.portfolioStatus.Closed,
        wealthify.enums.portfolioStatus.Closing,
        wealthify.enums.portfolioStatus.EnrolError,
        wealthify.enums.portfolioStatus.EnrolQueued,
        wealthify.enums.portfolioStatus.EnrolInProgress,
        wealthify.enums.portfolioStatus.Draft
    ];

    static eventSettings = Object.freeze({events: {
        continuePlanClick: 'continue-plan',
        removePlanClick: 'remove-plan',
    }});

    static get observedProps() {
        return [
            new wealthify.Prop('name', { required: true }),
            new wealthify.Prop('value', { type: wealthify.propTypes.Number() }),
            new wealthify.Prop('type', {
                required: true,
                type: wealthify.propTypes.ArrayItem(Object.keys(wealthify.enumsLocal.planType))
            }),
            new wealthify.Prop('risk-level', { type: wealthify.propTypes.ArrayItem(Object.values(wealthify.enums.investmentStyle)) }),
            new wealthify.Prop('theme', { type: wealthify.propTypes.ArrayItem(Object.values(wealthify.enums.theme)) }),
            new wealthify.Prop('portfolio-status', { type: wealthify.propTypes.ArrayItem(Object.values(wealthify.enums.portfolioStatus)) }),
        ];
    }

    get propCallbacks() {
        return {
            name: () => {
                this.dom.name.innerHTML = this.#name;
            },
            value: () => {
                this.#setValue();
            },
            type: () => {
                this.#setPlanType();
            },
            riskLevel: () => {
                this.#setRiskLevel();
            },
            theme: () => {
                this.#setTheme();
            },
            portfolioStatus: () => {
                this.#toggleContinueButton();
                this.#toggleRemoveButton();
                this.#setPlanType();
                this.#setTheme();
                this.#setRiskLevel();
                this.#updateCard();
            },
        };
    }

    get #name() {
        const customName = this.constructor.customPlanContent[this.type]?.name;
        // If plan is a "transfer", then we should display the name
        const useCustom = customName !== undefined && !this.name.toLowerCase().includes('transfer');

        return useCustom ? customName : this.name;
    }

    get #planType() {
        const customType = this.constructor.customPlanContent[this.type]?.type;

        return customType ?? wealthify.enumsLocal.planType[this.type];
    }

    get #planTypeName() {
        const customTypeName = this.constructor.customPlanContent[this.type]?.typeName;

        return customTypeName ?? wealthify.enums.accountTypeName[this.type];
    }

    get #accountGroupDescriptor() {
        return wealthify.enums.accountGroupDescriptor[wealthify.enums.accountTypeGroup[this.type]] ?? 'Plan';
    }

    get #isPlanEmpty() {
        return this.value === 0;
    }

    get #isPlanValueUndefined() {
        return this.value === undefined;
    }

    get #isPlanClosed() {
        return this.portfolioStatus === wealthify.enums.portfolioStatus.Closing || this.portfolioStatus === wealthify.enums.portfolioStatus.Closed;
    }

    get #isPlanCreated() {
        return this.portfolioStatus === wealthify.enums.portfolioStatus.Created;
    }

    get #isPlanDraft() {
        return this.portfolioStatus === wealthify.enums.portfolioStatus.Draft;
    }

    get #isPlanEnrolling() {
        return this.portfolioStatus === wealthify.enums.portfolioStatus.EnrolError ||
            this.portfolioStatus === wealthify.enums.portfolioStatus.EnrolQueued ||
            this.portfolioStatus === wealthify.enums.portfolioStatus.EnrolInProgress;
    }

    get #isInfoAdded() {
        return this.dom.info.tagName.toLowerCase() !== 'span';
    }

    get #areSecondaryMetricsSlotted() {
        const metrics = wealthify.type.array.isArray(this.dom.metric) ? this.dom.metric : [this.dom.metric];
        const hasSecondaryMetrics = metrics.every(metric => metric.tagName.toLowerCase() !== 'span');

        return hasSecondaryMetrics;
    }

    get #shouldAppearDisabled() {
        return this.constructor.disabledPlanStates.includes(this.portfolioStatus);
    }

    #setPlanType() {
        this.dom.typeLabel.innerHTML = this.#planTypeName;
        this.dom.valueHeading.innerHTML = this.#isPlanCreated ? 'Initial deposit amount' : this.constructor.accountTypeLabelMap[this.type] ?? 'Value';
        this.dom.planIcon.usage = this.#shouldAppearDisabled ? 'product-disabled' : `product-${this.#planType}`;
        this.dom.planIcon.type = this.#planType;
        this.dom.type.ariaLabel = this.#planTypeName;

        if (!this.#shouldAppearDisabled) {
            this.classList.add(`${this.styleSelector}--type-${this.#planType}`);
        }
    }

    #setRiskLevel() {
        if(this.riskLevel){
            const riskLevelName = Object.keys(wealthify.enums.investmentStyle).find(key => wealthify.enums.investmentStyle[key] === this.riskLevel) ?? '';
            const classListAction = this.riskLevel && (!this.#shouldAppearDisabled || this.#isPlanCreated || this.#isPlanDraft) ? 'remove' : 'add';
       
            this.dom.riskLevel.classList[classListAction]('u-display__none');
            this.dom.riskLevel.label = riskLevelName;
            this.dom.riskLevel.iconleft = `risk-level-${this.riskLevel}`;
        }

        this.#setInfoContainerVisibility();
    }

    #setTheme() {
        const themeName = Object.keys(wealthify.enums.theme).find(key => wealthify.enums.theme[key] === this.theme) ?? '';
        const classListAction = this.theme && this.theme !== wealthify.enums.theme.Original && (!this.#shouldAppearDisabled || this.#isPlanCreated || this.#isPlanDraft) ? 'remove' : 'add';

        this.#setInfoContainerVisibility();
        this.dom.theme.classList[classListAction]('u-display__none');
        this.dom.theme.label = `${themeName} plan`;
        this.dom.theme.iconleft = this.constructor.themeIconMap[this.theme];
    }

    #toggleContinueButton() {
        const buttonLabel = this.#isPlanCreated || this.#isPlanDraft ? 'Continue setting up' : `View ${this.#accountGroupDescriptor}`;

        this.dom.planLink.label = buttonLabel;
        this.dom.planLink.hide = this.#isPlanClosed || this.#isPlanEnrolling;
    }

    #toggleRemoveButton() {
        if (this.#isPlanCreated) {
            const planRemoveButtonLabel = `Remove ${this.#accountGroupDescriptor}`;

            this.dom.planRemove.label = planRemoveButtonLabel;
        }

        this.dom.planRemove.hide = !this.#isPlanCreated;
    }

    #updateCard() {
        if (this.#shouldAppearDisabled) {
            this.dom.root.disabledProp = true;
        }

        this.dom.root.inverted = this.dom.root.inverted || this.#shouldAppearDisabled;
    }

    #validateSecondaryMetricSlots() {
        if (this.#areSecondaryMetricsSlotted) {
            const metrics = Array.isArray(this.dom.metric) ? this.dom.metric : [this.dom.metric];
            const areMetricsValid = metrics.every(metric => {
                return metric.children.length === 2 &&
                    metric.children[0].tagName.toLowerCase() === 'h4' &&
                    metric.children[1].tagName.toLowerCase() === 'p';
            });

            if (!areMetricsValid) {
                wealthify.error.throwError(this.tagName, 'The slot content of "metric" must be a div containing a h4 element and a p element.');
            }
        }
    }

    #setSecondaryMetricsVisibilityAndStyles() {
        const metrics = Array.isArray(this.dom.metric) ? this.dom.metric : [this.dom.metric];
        const classListAction = this.#areSecondaryMetricsSlotted ? 'remove' : 'add';

        if (this.#areSecondaryMetricsSlotted) {
            metrics.forEach(metric => {
                const label = metric.children[0];
                const value = metric.children[1];

                label.classList.add(`${this.styleSelector}__metric-heading`);
                value.classList.add(`${this.styleSelector}__metric-value`, `${this.styleSelector}__metric-value-small`);

            });

            this.dom.secondaryMetrics.classList[classListAction]('u-display__none');
        }
    }

    #setInfoContainerVisibility() {
        const infoSlotContainerClassListAction = this.#isInfoAdded ? 'remove' : 'add';
        const infoRowClassListAction = this.#isInfoAdded || this.theme || this.riskLevel ? 'add' : 'remove';
        const infoContainerClassListAction = this.#isInfoAdded || this.theme || this.riskLevel ? 'remove' : 'add';

        this.dom.info.classList[infoSlotContainerClassListAction]('u-display__none');
        this.dom.infoRow.classList[infoRowClassListAction](`${this.styleSelector}__content-row--full-width`);
        this.dom.infoContainer.classList[infoContainerClassListAction]('u-display__none');
    }

    #setValue() {
        const isOpenAndEmpty = this.#isPlanEmpty && !this.#isPlanClosed && !this.#isPlanCreated;

        this.dom.metrics.classList.toggle('u-display__none', isOpenAndEmpty || this.#isPlanValueUndefined);

        if (isOpenAndEmpty || this.#isPlanValueUndefined) {
            return;
        }

        const value = wealthify.type.number.toCurrency(this.value);
        const valueUnits = value.toString().split('.');
        const pounds = valueUnits[0];
        const pence = valueUnits[1];
        const formattedValue = `<span class="${this.styleSelector}__metric-value-large">${pounds}</span><span class="${this.styleSelector}__metric-value-small">.${pence}</span>`;

        this.dom.value.innerHTML = formattedValue;
    }
});